/**
 * Giới hạn bộ nhớ + cache session Messenger (stale-while-revalidate).
 */
/** Số tin tối đa giữ trong messageList của cuộc chat đang mở. */
export const CHAT_MESSAGE_LIST_MAX = 300;
/** Số tin giữ trong cache theo từng group_id. */
export const CHAT_CACHE_MESSAGES_PER_GROUP = 60;
/** Số group tối đa trong cacheChatMessages (LRU theo lần truy cập). */
export const CHAT_CACHE_MAX_GROUPS = 8;
/** Số group tối đa trong sidebar store (load-more vẫn cắt trần). */
export const CHAT_GROUPS_STORE_MAX = 120;
/** Số lần fetch older tối đa khi jump-to-message. */
export const CHAT_SCROLL_TO_MESSAGE_MAX_FETCHES = 10;
/** Số toast popup đồng thời. */
export const CHAT_TOAST_QUEUE_MAX = 12;
/** Số notification chat trong sidebar. */
export const CHAT_SIDEBAR_NOTIFICATIONS_MAX = 80;
/** TTL cache session — sau đó soft-sync nền khi mở lại chat. */
export const CHAT_CACHE_TTL_MS = 45_000;
/** Cắt phía cũ (đầu list ASC) khi vượt max — giữ tin mới nhất. */
export function trimNewestMessages(list, max = CHAT_MESSAGE_LIST_MAX) {
    if (!Array.isArray(list) || list.length <= max) {
        return list;
    }
    return list.slice(list.length - max);
}
/**
 * Sau khi prepend tin cũ: nếu vượt max thì cắt phía mới (đuôi).
 * Trả về { list, trimmedTail } — trimmedTail=true nghĩa là đã mất tin mới hơn (hiện nút ↓).
 */
export function trimAfterPrependOlder(list, max = CHAT_MESSAGE_LIST_MAX) {
    if (!Array.isArray(list) || list.length <= max) {
        return { list, trimmedTail: false };
    }
    return { list: list.slice(0, max), trimmedTail: true };
}
export function lastMessageIdOf(list) {
    if (!Array.isArray(list) || list.length === 0) {
        return null;
    }
    const id = list[list.length - 1]?.id;
    const n = Number(id);
    return Number.isFinite(n) ? n : null;
}
/** Chuẩn hoá entry (array cũ hoặc object metadata). */
export function normalizeCacheEntry(raw) {
    if (!raw) {
        return null;
    }
    if (Array.isArray(raw)) {
        if (raw.length === 0) {
            return null;
        }
        const messages = trimNewestMessages(raw, CHAT_CACHE_MESSAGES_PER_GROUP);
        return {
            messages,
            lastMessageId: lastMessageIdOf(messages),
            fetchedAt: 0,
            hasMoreOlder: true,
        };
    }
    if (typeof raw === "object") {
        const row = raw;
        const messages = Array.isArray(row.messages)
            ? trimNewestMessages(row.messages, CHAT_CACHE_MESSAGES_PER_GROUP)
            : [];
        if (messages.length === 0) {
            return null;
        }
        return {
            messages,
            lastMessageId: row.lastMessageId != null ? Number(row.lastMessageId) : lastMessageIdOf(messages),
            fetchedAt: Number(row.fetchedAt) || 0,
            hasMoreOlder: row.hasMoreOlder !== false,
        };
    }
    return null;
}
export function getCacheMessages(cache, groupId) {
    if (cache == null || groupId == null || String(groupId) === "") {
        return [];
    }
    const entry = normalizeCacheEntry(cache[String(groupId)]);
    return entry ? entry.messages.slice() : [];
}
export function getCacheEntry(cache, groupId) {
    if (cache == null || groupId == null || String(groupId) === "") {
        return null;
    }
    return normalizeCacheEntry(cache[String(groupId)]);
}
export function isCacheFresh(entry, ttlMs = CHAT_CACHE_TTL_MS) {
    if (!entry || !entry.fetchedAt) {
        return false;
    }
    return Date.now() - entry.fetchedAt < ttlMs;
}
/**
 * Soft sync khi: hết TTL, unread > 0, force, hoặc cache không có fetchedAt (legacy).
 */
export function shouldSoftSyncMessages(_entry, _options) {
    // Luôn soft-sync khi mở hội thoại: cache 45s + unread=0 từng bỏ tin mới
    // (socket miss / bấm thông báo khi đang ở đúng nhóm).
    return true;
}
export function buildCacheEntry(messages, options) {
    const trimmed = trimNewestMessages(messages, CHAT_CACHE_MESSAGES_PER_GROUP);
    return {
        messages: trimmed,
        lastMessageId: lastMessageIdOf(trimmed),
        fetchedAt: options?.fetchedAt != null ? options.fetchedAt : Date.now(),
        hasMoreOlder: options?.hasMoreOlder !== false,
    };
}
export function writeCacheEntry(cache, groupId, messages, options) {
    const key = String(groupId);
    const next = { ...(cache || {}) };
    next[key] = buildCacheEntry(messages, options);
    return pruneCacheChatMessages(next, key);
}
/** LRU cache: giữ order keys theo lần dùng gần nhất (cuối = mới nhất). */
export function pruneCacheChatMessages(cache, touchGroupId, options) {
    const maxGroups = options?.maxGroups ?? CHAT_CACHE_MAX_GROUPS;
    const maxPerGroup = options?.maxPerGroup ?? CHAT_CACHE_MESSAGES_PER_GROUP;
    const next = { ...cache };
    if (touchGroupId != null && String(touchGroupId) !== "") {
        const key = String(touchGroupId);
        const entry = normalizeCacheEntry(next[key]);
        if (entry) {
            entry.messages = trimNewestMessages(entry.messages, maxPerGroup);
            entry.lastMessageId = lastMessageIdOf(entry.messages);
            delete next[key];
            next[key] = entry;
        }
    }
    const keys = Object.keys(next);
    if (keys.length > maxGroups) {
        const drop = keys.slice(0, keys.length - maxGroups);
        for (const k of drop) {
            delete next[k];
        }
    }
    for (const k of Object.keys(next)) {
        const entry = normalizeCacheEntry(next[k]);
        if (entry) {
            entry.messages = trimNewestMessages(entry.messages, maxPerGroup);
            entry.lastMessageId = lastMessageIdOf(entry.messages);
            next[k] = entry;
        }
        else {
            delete next[k];
        }
    }
    return next;
}
export function capArrayTail(list, max) {
    if (!Array.isArray(list) || list.length <= max) {
        return list;
    }
    return list.slice(list.length - max);
}
/** Normalize cả map khi hydrate từ Vuex (legacy array entries). */
export function coerceMessageCacheMap(raw) {
    if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
        return {};
    }
    const out = {};
    for (const [key, value] of Object.entries(raw)) {
        const entry = normalizeCacheEntry(value);
        if (entry) {
            out[key] = entry;
        }
    }
    return pruneCacheChatMessages(out);
}
